import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Building2, Lock, User, Eye, EyeOff, AlertCircle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const ClinicaLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetUser, setResetUser] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    if (window.posthog) {
      window.posthog.opt_out_capturing();
      window.posthog.stopSessionRecording && window.posthog.stopSessionRecording();
    }
    return () => { if (window.posthog) window.posthog.opt_in_capturing(); };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error de autenticación');
      }

      // Store token and user info
      localStorage.setItem('clinica_token', data.token);
      localStorage.setItem('clinica_user', JSON.stringify({
        username: data.username,
        nombre_clinica: data.nombre_clinica,
        clinica_id: data.clinica_id,
        rol: data.rol
      }));

      // Redirect to clinic portal
      navigate('/clinica/portal');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetError(''); setResetMsg(''); setResetLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: resetUser })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setResetMsg('Se ha enviado una solicitud al administrador. Recibirá su nueva contraseña en breve.');
    } catch (err) {
      setResetError(err.message);
    }
    setResetLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1C1C1A] to-[#1a2a42] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-4">
            <Building2 className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-light text-white mb-2">
            Portal de Clínicas y Centros Colaboradores
          </h1>
          <p className="text-white/60 text-sm">
            Dr. Ángel Norro - Cirugía Plástica y Estética
          </p>
        </div>

        {/* Login Card */}
        <Card className="p-8">
          {showReset ? (
            /* Password Reset Form */
            <div>
              <h2 className="text-xl font-semibold text-[#1C1C1A] mb-2 text-center">Recuperar contraseña</h2>
              <p className="text-sm text-gray-500 text-center mb-6">Introduzca su nombre de usuario y le enviaremos una nueva contraseña.</p>

              {resetMsg && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                  <CheckCircle2 size={18} /><span className="text-sm">{resetMsg}</span>
                </div>
              )}
              {resetError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle size={18} /><span className="text-sm">{resetError}</span>
                </div>
              )}

              {!resetMsg && (
                <form onSubmit={handleReset}>
                  <div className="relative mb-4">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input type="text" value={resetUser} onChange={e => setResetUser(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Usuario o email" required data-testid="reset-username" />
                  </div>
                  <Button type="submit" disabled={resetLoading} className="w-full bg-[#B8975A] hover:bg-[#9A7D4A] text-white py-3" data-testid="reset-submit">
                    {resetLoading ? 'Enviando...' : 'Solicitar nueva contraseña'}
                  </Button>
                </form>
              )}

              <button onClick={() => { setShowReset(false); setResetMsg(''); setResetError(''); setResetUser(''); }}
                className="flex items-center gap-1 text-sm text-[#B8975A] hover:underline mt-4 mx-auto">
                <ArrowLeft size={14} />Volver al inicio de sesión
              </button>
            </div>
          ) : (
            /* Login Form */
            <>
              <form onSubmit={handleLogin}>
                <h2 className="text-xl font-semibold text-[#1C1C1A] mb-6 text-center">
                  Acceso para Clínicas
                </h2>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                    <AlertCircle size={18} />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Usuario o email</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="Usuario o email" required data-testid="login-username" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1">Contraseña</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        placeholder="Contraseña" required data-testid="login-password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-text-secondary">
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={loading} className="w-full mt-6 bg-[#B8975A] hover:bg-[#9A7D4A] text-white py-3" data-testid="login-submit">
                  {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button onClick={() => setShowReset(true)} className="text-sm text-[#B8975A] hover:underline" data-testid="forgot-password-link">
                  ¿Olvidó su contraseña?
                </button>
              </div>
            </>
          )}

          <div className="mt-6 pt-6 border-t text-center">
            <p className="text-sm text-text-muted">
              ¿No tienes acceso? Contacta con{' '}
              <a href="tel:+34952764547" className="text-[#B8975A] hover:underline">952 76 45 47</a>
            </p>
          </div>
        </Card>

        {/* Back to main site */}
        <div className="text-center mt-6">
          <a href="/" className="text-white/60 hover:text-white text-sm transition-colors">
            ← Volver a la web principal
          </a>
        </div>
      </div>
    </div>
  );
};

export default ClinicaLogin;
